import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/**
 * Catches render-time errors in a page so one broken page shows a recoverable
 * message instead of white-screening the whole admin console.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[admin] page crashed:', error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="grid min-h-[60vh] place-items-center">
        <div className="border-border bg-surface shadow-card w-full max-w-md rounded-2xl border p-8 text-center">
          <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-red-50 text-red-600 ring-1 ring-inset ring-red-100">
            <svg
              className="h-7 w-7"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            </svg>
          </div>
          <h2 className="text-ink text-lg font-bold tracking-tight">Something went wrong</h2>
          <p className="text-ink-muted mt-1.5 text-sm">
            This page hit an unexpected error and could not be displayed.
          </p>
          <p className="bg-surface-subtle mt-3 break-words rounded-lg px-3 py-2 text-left font-mono text-xs text-red-600">
            {error.message}
          </p>
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            className="bg-gold-500 text-zone-900 hover:bg-gold-400 mt-4 rounded-xl px-4 py-2 text-sm font-semibold transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}
